import type {
  FilterConditionDTO,
  OffsetPageQueryDTO,
} from "@/shared/contracts/base";
import {
  findCondition,
  removeConditions,
  upsertCondition,
} from "@/shared/contracts/query-helpers";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";
import { Button, DatePicker, Input, useDebounceCallback } from "@devhop/ui";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { format } from "date-fns";
import { XIcon } from "lucide-react";
import { useEffect, useState } from "react";

export function BookingsFilter() {
  const nav = useNavigate({ from: "/app/private-lessons/bookings" });
  const search = useSearch({
    from: "/app/private-lessons/bookings",
  }) as OffsetPageQueryDTO;
  const filters = (search.filters as FilterConditionDTO[] | undefined) ?? [];

  const studentNameFilter = findCondition(filters, "studentName");
  const studentMemberIdFilter = findCondition(filters, "studentMemberId");
  const dateFromFilter = findCondition(filters, "date");
  const statusFilter = findCondition(filters, "status");

  const [studentNameValue, setStudentNameValue] = useState<string>(
    (studentNameFilter?.value as string) || "",
  );
  const [studentMemberIdValue, setStudentMemberIdValue] = useState<string>(
    (studentMemberIdFilter?.value as string) || "",
  );
  const [dateValue, setDateValue] = useState<string>(
    (dateFromFilter?.value && typeof dateFromFilter.value === "string"
      ? dateFromFilter.value
      : "") || "",
  );

  useEffect(
    () => setStudentNameValue((studentNameFilter?.value as string) || ""),
    [studentNameFilter?.value],
  );
  useEffect(
    () =>
      setStudentMemberIdValue((studentMemberIdFilter?.value as string) || ""),
    [studentMemberIdFilter?.value],
  );
  useEffect(() => {
    setDateValue(
      dateFromFilter?.value && typeof dateFromFilter.value === "string"
        ? dateFromFilter.value
        : "",
    );
  }, [dateFromFilter?.value]);

  const debouncedStudentName = useDebounceCallback((val: string) => {
    setStudentNameValue(val);
    let nextFilters: FilterConditionDTO[] | undefined = filters;
    if (val) {
      nextFilters = upsertCondition(filters, {
        field: "studentName",
        op: "contains",
        value: val,
      });
    } else {
      nextFilters = removeConditions(filters, "studentName");
    }
    nav({
      search: {
        ...search,
        offset: 0,
        filters: nextFilters?.length ? nextFilters : undefined,
      },
    });
  }, 400);

  const debouncedStudentMemberId = useDebounceCallback((val: string) => {
    setStudentMemberIdValue(val);
    let nextFilters: FilterConditionDTO[] | undefined = filters;
    if (val) {
      nextFilters = upsertCondition(filters, {
        field: "studentMemberId",
        op: "contains",
        value: val,
      });
    } else {
      nextFilters = removeConditions(filters, "studentMemberId");
    }
    nav({
      search: {
        ...search,
        offset: 0,
        filters: nextFilters?.length ? nextFilters : undefined,
      },
    });
  }, 400);

  const statusValue =
    typeof statusFilter?.value === "string" ? statusFilter.value : "";

  const hasFilter =
    !!studentNameFilter?.value ||
    !!studentMemberIdFilter?.value ||
    !!dateFromFilter?.value ||
    !!statusValue;

  return (
    <div className="mb-4 flex flex-col gap-2 px-2">
      <div className="flex items-center justify-between gap-4 max-sm:flex-col">
        <div className="flex flex-1 flex-wrap items-center gap-2">
          <Input
            placeholder="ຄົ້ນຫາຊື່ນັກຮຽນ..."
            defaultValue={studentNameValue}
            onChange={(e) => debouncedStudentName(e.target.value)}
            className="h-8 sm:max-w-2xs"
          />
          <Input
            placeholder="ຄົ້ນຫາເລກສະມາຊິກ..."
            defaultValue={studentMemberIdValue}
            onChange={(e) => debouncedStudentMemberId(e.target.value)}
            className="h-8 sm:max-w-2xs"
          />
          <DatePicker
            value={dateValue ? new Date(dateValue) : undefined}
            onChange={(date) => {
              const dateStr = date ? format(date, "yyyy-MM-dd") : "";
              setDateValue(dateStr);
              let nextFilters: FilterConditionDTO[] | undefined = filters;
              if (dateStr) {
                nextFilters = upsertCondition(filters, {
                  field: "date",
                  op: "gte",
                  value: dateStr,
                });
              } else {
                nextFilters = removeConditions(filters, "date");
              }
              nav({
                search: {
                  ...search,
                  offset: 0,
                  filters: nextFilters?.length ? nextFilters : undefined,
                },
              });
            }}
            className="h-8 sm:max-w-2xs"
          />

          <SimpleSelect
            value={statusValue || "all"}
            onValueChange={(val) => {
              let nextFilters: FilterConditionDTO[] | undefined = filters;
              if (val === "all") {
                nextFilters = removeConditions(filters, "status");
              } else {
                nextFilters = upsertCondition(filters, {
                  field: "status",
                  op: "eq",
                  value: val,
                });
              }
              nav({
                search: {
                  ...search,
                  offset: 0,
                  filters: nextFilters?.length ? nextFilters : undefined,
                },
              });
            }}
            placeholder="ສະຖານະ"
            className="h-8 w-full sm:w-32"
            options={[
              { value: "all", label: "ທັງໝົດ" },
              { value: "scheduled", label: "ຈອງແລ້ວ" },
              { value: "completed", label: "ສຳເລັດ" },
              { value: "cancelled", label: "ຍົກເລີກ" },
            ]}
          />
        </div>
        {hasFilter && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() =>
              nav({ search: { ...search, filters: undefined, offset: 0 } })
            }
            className="max-sm:w-full"
          >
            <XIcon className="h-4 w-4" /> ລ້າງ
          </Button>
        )}
      </div>
    </div>
  );
}
